import { notification, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import React from 'react';

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes in milliseconds
const VERSION_STORAGE_KEY = 'pitstop_app_version';
const VERSION_CHECK_ENDPOINT =
  'https://us-central1-pitstop-dashboard-prod.cloudfunctions.net/getLatestHostingVersion';

class VersionChecker {
  constructor() {
    this.intervalId = null;
    this.isChecking = false;
  }

  // Get the current stored version from localStorage
  getStoredVersion() {
    try {
      const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);
      return storedVersion || null;
    } catch (error) {
      console.error('Error getting stored version:', error);
      return null;
    }
  }

  // Save version string to localStorage
  saveVersion(version) {
    try {
      localStorage.setItem(VERSION_STORAGE_KEY, version);
    } catch (error) {
      console.error('Error saving version:', error);
    }
  }

  // Perform a hard refresh with cache clearing
  async forceRefresh() {
    try {
      // 1. Clear Service Worker caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map((cacheName) => caches.delete(cacheName))
        );
      }

      // 2. Unregister all service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations.map((registration) => registration.unregister())
        );
      }

      // 3. Clear session storage
      sessionStorage.clear();

      // 4. Clear localStorage except for the version data
      const versionData = this.getStoredVersion();
      localStorage.clear();
      if (versionData) {
        this.saveVersion(versionData);
      }

      // 5. Add cache-busting to the reload URL
      const timestamp = new Date().getTime();
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('_v', timestamp);

      // 6. Force hard refresh with cache bypass
      // Note: reload(true) is deprecated but still widely supported
      // Using multiple approaches for maximum compatibility
      if (window.location.reload.length > 0) {
        // Newer browsers
        window.location.href = currentUrl.toString();
      } else {
        // Older browsers - force reload with true parameter
        window.location.reload(true);
      }
    } catch (error) {
      console.error('Error during force refresh:', error);
      // Fallback to simple reload
      window.location.reload();
    }
  }

  // Show notification to user about new version
  showUpdateNotification() {
    const key = 'version-update-notification';

    notification.info({
      key,
      message: 'New Version Available',
      description: 'A new version of the Pitstop dashboard just went out',
      duration: 0, // Don't auto-close
      placement: 'topRight',
      icon: <ReloadOutlined style={{ color: '#1890ff' }} />,
      btn: (
        <Button
          type="primary"
          size="small"
          onClick={async () => {
            notification.destroy(key);
            await this.forceRefresh();
          }}
        >
          Refresh
        </Button>
      ),
      style: {
        width: 400,
      },
    });
  }

  // Check for new version
  async checkVersion() {
    if (this.isChecking) {
      return; // Prevent concurrent checks
    }

    this.isChecking = true;

    try {
      const response = await fetch(VERSION_CHECK_ENDPOINT, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const latestVersionData = await response.json();
      const storedVersion = this.getStoredVersion();
      const latestVersion = latestVersionData.version;

      if (!storedVersion) {
        // First time - save the version
        this.saveVersion(latestVersion);
      } else if (storedVersion !== latestVersion) {
        // Version has changed - show notification and save new version
        this.showUpdateNotification();
        this.saveVersion(latestVersion);
      }
    } catch (error) {
      console.error('Error checking version:', error);
    } finally {
      this.isChecking = false;
    }
  }

  // Start the version checking interval
  start() {
    // Perform initial check
    this.checkVersion();

    // Set up interval for periodic checks
    this.intervalId = setInterval(() => {
      this.checkVersion();
    }, VERSION_CHECK_INTERVAL);
  }

  // Stop the version checking interval
  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}

// Create and export a singleton instance
const versionChecker = new VersionChecker();
export default versionChecker;
